"use client";

import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import { Search, X, RotateCcw } from "lucide-react";
import { Button } from "./button.js";
import { Input } from "./input.js";
import { SearchFilterChips } from "./SearchFilterChips.js";
import { SearchFilterPopover } from "./SearchFilterPopover.js";
import { SearchResultGroupCollapsible } from "./SearchResultGroupCollapsible.js";
import { SearchResultGroupSidebar } from "./SearchResultGroupSidebar.js";
import { SearchSuggestions } from "./SearchSuggestions.js";
import { SearchLoadingSkeleton } from "./SearchLoadingSkeleton.js";

export interface SearchResult {
  objectID: string;
  title?: string;
  name?: string;
  description?: string;
  type: string;
  url: string;
  iconType: string;
  subtitle?: string;
  tags?: string[];
  highlightedTitle?: string | null;
  highlightedContent?: string | null;
  status?: string;
  priority?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UseAlgoliaSearchReturn {
  query: string;
  results: SearchResult[];
  isLoading: boolean;
  hasPersistedResults: boolean;
  filters: any;
  facets: any;
  /**
   * Run a search. The return value is deliberately UNCONSTRAINED (`unknown`):
   * these components read results from the hook's state, while other callers
   * (Hub's command palette) await the matched rows. Pinning this to
   * `Promise<void>` made the richer signature un-assignable here.
   */
  searchAlgolia: (query: string, filters?: any) => Promise<unknown>;
  handleResultClick: (result: SearchResult, onClose?: () => void) => void;
  clearSearch: () => void;
  clearFilters: () => void;
  setFilters: (filters: any) => void;
  restoreSession: () => void;
  getHighlightedParts: (text: string) => Array<{ text: string; isHighlighted: boolean }>;
  getResultIcon: (type: string) => React.ReactNode;
  securedKeyError?: Error | null;
}

interface SearchModalProps {
  useAlgoliaSearch: () => UseAlgoliaSearchReturn;
  placeholder?: string;
  onClose?: () => void;
}

// Memoized results section to prevent re-renders during search input
interface SearchResultsSectionProps {
  isLoading: boolean;
  showResults: boolean;
  showEmpty: boolean;
  results: SearchResult[];
  localQuery: string;
  getResultIcon: (type: string) => React.ReactNode;
  renderHighlightedText: (text: string) => React.ReactNode;
  onResultClick: (result: SearchResult) => void;
  enabledGroups: Record<string, boolean>;
  onSuggestionClick: (suggestion: string) => void;
}

const SearchResultsSection = memo(function SearchResultsSection({
  isLoading,
  showResults,
  showEmpty,
  results,
  localQuery,
  getResultIcon,
  renderHighlightedText,
  onResultClick,
  enabledGroups,
  onSuggestionClick,
}: SearchResultsSectionProps) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      {isLoading && <SearchLoadingSkeleton count={5} />}

      {showEmpty && (
        <SearchSuggestions
          query={localQuery}
          onSuggestionClick={onSuggestionClick}
        />
      )}

      {!isLoading && showResults && results.length > 0 && (
        <div className="px-4 py-4">
          <SearchResultGroupCollapsible
            results={results}
            getResultIcon={getResultIcon}
            renderHighlightedText={renderHighlightedText}
            onResultClick={onResultClick}
            enabledGroups={enabledGroups}
          />
        </div>
      )}

      {!isLoading && !showResults && (
        <div className="px-4 py-12 text-center text-gray-500">
          <p className="text-sm">Start typing to search...</p>
        </div>
      )}
    </div>
  );
});

interface FilterChip {
  type: string;
  value: string;
  label: string;
}

export function SearchModal({
  useAlgoliaSearch,
  placeholder = "Search for tickets, contacts, time entries...",
  onClose,
}: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localQuery, setLocalQuery] = useState("");
  const [enabledGroups, setEnabledGroups] = useState<Record<string, boolean>>({});
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    query,
    results,
    isLoading,
    hasPersistedResults,
    filters,
    facets,
    searchAlgolia,
    handleResultClick,
    clearSearch,
    clearFilters,
    setFilters,
    getHighlightedParts,
    getResultIcon,
    securedKeyError,
  } = useAlgoliaSearch();

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounced search
  const handleQueryChange = useCallback(
    (value: string) => {
      setLocalQuery(value);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        searchAlgolia(value, filters);
      }, 300);
    },
    [searchAlgolia, filters]
  );

  // Render highlighted text
  const renderHighlightedText = useCallback(
    (text: string) => {
      const parts = getHighlightedParts(text);
      return parts.map((part, idx) =>
        part.isHighlighted ? (
          <mark key={idx} className="bg-yellow-200 font-semibold">
            {part.text}
          </mark>
        ) : (
          <span key={idx}>{part.text}</span>
        )
      );
    },
    [getHighlightedParts]
  );

  // Handle result click
  const handleResultClickWithClose = useCallback(
    (result: SearchResult) => {
      handleResultClick(result, onClose);
    },
    [handleResultClick, onClose]
  );

  // Build filter chips from current filters
  const filterChips = useMemo(() => {
    const chips: FilterChip[] = [];

    if (filters.types && filters.types.length > 0) {
      filters.types.forEach((type: string) => {
        chips.push({
          type: "types",
          value: type,
          label: `Type: ${type}`,
        });
      });
    }

    if (filters.status && filters.status.length > 0) {
      filters.status.forEach((status: string) => {
        chips.push({
          type: "status",
          value: status,
          label: `Status: ${status}`,
        });
      });
    }

    if (filters.priority && filters.priority.length > 0) {
      filters.priority.forEach((priority: string) => {
        chips.push({
          type: "priority",
          value: priority,
          label: `Priority: ${priority}`,
        });
      });
    }

    if (filters.tags && filters.tags.length > 0) {
      filters.tags.forEach((tag: string) => {
        chips.push({
          type: "tags",
          value: tag,
          label: `Tag: ${tag}`,
        });
      });
    }

    return chips;
  }, [filters]);

  // Handle filter chip removal
  const handleRemoveChip = useCallback(
    (type: string, value: string) => {
      const currentFilters = filters[type] || [];
      const newFilters = currentFilters.filter((v: string) => v !== value);

      setFilters({
        ...filters,
        [type]: newFilters.length > 0 ? newFilters : undefined,
      });

      // Re-search with updated filters
      searchAlgolia(localQuery, {
        ...filters,
        [type]: newFilters.length > 0 ? newFilters : undefined,
      });
    },
    [filters, setFilters, searchAlgolia, localQuery]
  );

  // Handle clear all filters
  const handleClearAllChips = useCallback(() => {
    clearFilters();
    searchAlgolia(localQuery, {});
  }, [clearFilters, searchAlgolia, localQuery]);

  // Handle filter change from popover
  const handleFilterChange = useCallback(
    (newFilters: any) => {
      setFilters(newFilters);
      searchAlgolia(localQuery, newFilters);
    },
    [setFilters, searchAlgolia, localQuery]
  );

  // Handle filter change from popover
  const handleClearFiltersPopover = useCallback(() => {
    clearFilters();
    searchAlgolia(localQuery, {});
  }, [clearFilters, searchAlgolia, localQuery]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setLocalQuery(suggestion);
      handleQueryChange(suggestion);
    },
    [handleQueryChange]
  );

  const showResults = query.length > 0 || hasPersistedResults;
  const showEmpty = !isLoading && showResults && results.length === 0;

  // Build URL for full-page search with current query and filters
  const getFullPageSearchUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (localQuery) params.append("q", localQuery);
    if (filters.types?.length) params.append("types", filters.types.join(","));
    if (filters.status?.length) params.append("status", filters.status.join(","));
    if (filters.priority?.length) params.append("priority", filters.priority.join(","));
    if (filters.tags?.length) params.append("tags", filters.tags.join(","));
    
    const queryString = params.toString();
    return `/search${queryString ? `?${queryString}` : ""}`;
  }, [localQuery, filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Error banner */}
      {securedKeyError && (
        <div className="flex-shrink-0 bg-red-50 border-b border-red-200 px-4 py-3">
          <p className="text-sm text-red-800">
            <strong>Search unavailable:</strong> {securedKeyError.message}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200">
        {/* Search input row */}
        <div className="px-4 py-3 flex items-center gap-2">
          <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <Input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={localQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            disabled={!!securedKeyError}
            className="flex-1 border-0 focus-visible:ring-0 focus-visible:outline-none px-0 text-base disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {localQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setLocalQuery("");
                clearSearch();
              }}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setLocalQuery("");
              clearSearch();
            }}
            className="h-8 w-8 p-0"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter chips row */}
        {filterChips.length > 0 && (
          <SearchFilterChips
            chips={filterChips}
            onRemoveChip={handleRemoveChip}
            onClearAll={handleClearAllChips}
          />
        )}

        {/* Filter popover button row */}
        <div className="px-4 py-2 flex items-center gap-2 border-t border-gray-100">
          <SearchFilterPopover
            facets={facets}
            selectedFilters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFiltersPopover}
          />
        </div>
      </div>

      {/* Main content with sidebar */}
      <div className="flex flex-1 overflow-hidden min-h-0">
        {/* Sidebar with group filters */}
        <SearchResultGroupSidebar
          results={results}
          onGroupToggle={(type, enabled) => {
            setEnabledGroups(prev => ({
              ...prev,
              [type]: enabled,
            }));
          }}
          enabledGroups={enabledGroups}
          getResultIcon={getResultIcon}
        />

        {/* Memoized results section - only re-renders when results/loading state changes */}
        <SearchResultsSection
          isLoading={isLoading}
          showResults={showResults}
          showEmpty={showEmpty}
          results={results}
          localQuery={localQuery}
          getResultIcon={getResultIcon}
          renderHighlightedText={renderHighlightedText}
          onResultClick={handleResultClickWithClose}
          enabledGroups={enabledGroups}
          onSuggestionClick={handleSuggestionClick}
        />
      </div>

      {/* Footer with link to full-page search */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <a
          href={getFullPageSearchUrl()}
          onClick={(e) => {
            e.preventDefault();
            onClose?.();
            window.location.href = getFullPageSearchUrl();
          }}
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 hover:underline transition-colors"
        >
          <span>Open in full-page search</span>
          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  );
}
